import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import './App.css';

// Base URL dell'API (vuota = stesso origin, gestito da nginx)
const API_URL = '';

// Festività fisse usate come FALLBACK se l'API non è raggiungibile.
// Nota: Pasqua/Pasquetta sono rimosse perché cambiano ogni anno
// e devono essere inserite manualmente nel DB dall'admin.
const ITALIAN_HOLIDAYS_FALLBACK = [
  { date: '01-01', name: 'Capodanno' },
  { date: '01-06', name: 'Epifania' },
  { date: '04-25', name: 'Festa Liberazione' },
  { date: '05-01', name: 'Festa Lavoro' },
  { date: '06-02', name: 'Repubblica' },
  { date: '08-15', name: 'Assunzione' },
  { date: '11-01', name: 'Ognissanti' },
  { date: '12-08', name: 'Immacolata' },
  { date: '12-25', name: 'Natale' },
  { date: '12-26', name: 'S.Stefano' },
];

const MONTHS = ['', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const DAYS_OF_WEEK = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const isRedState = (v) => ['M','PERM','A','PIOG','TS','REP'].includes(v);
const isBlueState = (v) => v === 'F';
const ownsDayCommon = (g, cant, firstCantiereId) => {
  if (!g) return false;
  if (g.cantieri && g.cantieri.length > 0) {
    return g.cantieri[0].cantiere_id === cant.id;
  }
  return cant.id === firstCantiereId;
};

async function apiCall(endpoint, options = {}) {
  const token = sessionStorage.getItem('token');
  const authHeader = options.headers?.Authorization || (token ? `Bearer ${token}` : '');
  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...options.headers, ...(authHeader ? { Authorization: authHeader } : {}) },
  });
  const text = await res.text();
  if (!res.ok) {
    console.error(`API Error ${res.status}:`, text);
    throw new Error(text || `Errore HTTP ${res.status}`);
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function OreInput({ label, value, onChange }) {
  const step = (delta) => {
    const current = parseFloat(value) || 0;
    const next = Math.max(0, Math.min(24, Math.round((current + delta) * 2) / 2));
    onChange(next);
  };
  return (
    <div className="form-field ore-field">
      <label>{label}</label>
      <div className="ore-stepper">
        <button type="button" className="ore-step" tabIndex={-1} onClick={() => step(-0.5)}>−</button>
        <input
          type="number"
          step="0.5"
          min="0"
          max="24"
          inputMode="decimal"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
        <button type="button" className="ore-step" tabIndex={-1} onClick={() => step(0.5)}>+</button>
      </div>
    </div>
  );
}

function PageHeader() {
  const location = useLocation();
  const titles = {
    '/': 'Dashboard',
    '/workers': 'Operai',
    '/sites': 'Cantieri',
    '/reports': 'Report',
    '/users': 'Utenti',
    '/settings': 'Impostazioni',
  };
  const title = titles[location.pathname];
  if (!title) return null;
  return (
    <div className="page-header">
      <span className="page-header-title">{title}</span>
    </div>
  );
}

/**
 * Carica le festività dal database e le converte nel formato MM-DD usato
 * dai componenti. Se l'API fallisce, usa la lista statica di fallback.
 */
function useFestivita(year) {
  const [festivita, setFestivita] = useState(ITALIAN_HOLIDAYS_FALLBACK);
  useEffect(() => {
    if (!year) return;
    apiCall(`/api/festivita?anno=${year}`)
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          // Il DB restituisce una stringa data (es. 2026-05-01T00:00:00.000Z) → estraiamo solo 'MM-DD'
          setFestivita(data.map(f => ({
            date: String(f.data).substring(5, 10),
            name: f.nome
          })));
        }
      })
      .catch(() => { /* mantiene il fallback */ });
  }, [year]);
  return festivita;
}

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [code2fa, setCode2fa] = useState('');
  const [error, setError] = useState('');
  const [requires2fa, setRequires2fa] = useState(false);
  const [tempToken, setTempToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = {};
      if (requires2fa && tempToken) {
        headers['Authorization'] = `Bearer ${tempToken}`;
      }
      const body = requires2fa 
        ? JSON.stringify({ username, password, code2fa })
        : JSON.stringify({ username, password });
      const result = await apiCall('/api/login', { method: 'POST', body, headers });
      if (result.token) {
        sessionStorage.setItem('token', result.token);
        sessionStorage.setItem('role', result.role);
        onLogin(result.role);
      } else if (result.requires2fa) {
        setRequires2fa(true);
        setTempToken(result.tempToken);
        setError('');
      } else {
        setError(result.error || 'Errore');
      }
    } catch (err) {
      setError('Credenziali errate');
    }
  };

  return (
    <div className="login-page">
      <form className="login-form" onSubmit={handleSubmit}>
        <h2>Gestione Presenze</h2>
        {error && <div className="error-box">{error}</div>}
        <input type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} required disabled={requires2fa} />
        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required disabled={requires2fa} />
        {requires2fa && (
          <input type="text" placeholder="Codice Google Authenticator" value={code2fa} onChange={e => setCode2fa(e.target.value)} required maxLength={6} />
        )}
        <button type="submit">{requires2fa ? 'Verifica 2FA' : 'Accedi'}</button>
      </form>
    </div>
  );
}

function Dashboard() {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [workers, setWorkers] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editForm, setEditForm] = useState({ ore_lavorate: 0, ore_straordinario: 0, ore_trasferta: 0, ore_reperibilita: 0, ore_permesso: 0, stato: 'Lavorato' });
  const [cantieriList, setCantieriList] = useState([]);
  const [selectedCantieri, setSelectedCantieri] = useState([]);
  const festivita = useFestivita(year);
  const [quickMode, setQuickMode] = useState(false);
  const [selectedWorkers, setSelectedWorkers] = useState(new Set());
  const [quickEntry, setQuickEntry] = useState(null);
  const [bulkDay, setBulkDay] = useState(1);
  const [bulkForm, setBulkForm] = useState({ ore_lavorate: 8, ore_straordinario: 0, ore_trasferta: 0, ore_reperibilita: 0, ore_permesso: 0, stato: 'Lavorato' });
  const [showBulkPanel, setShowBulkPanel] = useState(false);

  useEffect(() => { loadData(); }, [month, year]);

  const loadData = async () => {
    try {
      const [w, a, c] = await Promise.all([
        apiCall('/api/operai'),
        apiCall(`/api/presenze?mese=${month}&anno=${year}`),
        apiCall('/api/cantieri')
      ]);
      setWorkers(w.sort((a, b) => a.id - b.id));
      setAttendance(a);
      setCantieriList(c);
    } catch (err) { console.error(err); }
  };

  const getDaysInMonth = (m, y) => new Date(y, m, 0).getDate();

  const isWeekend = (day) => new Date(year, month - 1, day).getDay() === 0 || new Date(year, month - 1, day).getDay() === 6;

  const isHoliday = (day) => {
    const d = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return festivita.find(h => h.date === d);
  };

  const getDayName = (day) => DAYS_OF_WEEK[new Date(year, month - 1, day).getDay()];

  const getAttendanceFor = (workerId, day) => {
    const d = new Date(year, month - 1, day);
    const dStr = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
    const result = attendance.find(a => a.operaio_id === workerId && a.data && a.data.startsWith(dStr));
    return result;
  };

  const handleDayClick = (worker, day) => {
    const existing = getAttendanceFor(worker.id, day);
    const formData = existing ? {
      ore_lavorate: Number(existing.ore_lavorate) || 0,
      ore_straordinario: Number(existing.ore_straordinario) || 0,
      ore_trasferta: Number(existing.ore_trasferta) || 0,
      ore_reperibilita: Number(existing.ore_reperibilita) || 0,
      ore_permesso: Number(existing.ore_permesso) || 0,
      stato: existing.stato || 'Lavorato'
    } : { ore_lavorate: 0, ore_straordinario: 0, ore_trasferta: 0, ore_reperibilita: 0, ore_permesso: 0, stato: 'Lavorato' };
    const cantieriData = existing?.cantieri ? existing.cantieri.map(c => ({ cantiere_id: c.cantiere_id, ore: c.ore })) : [];

    if (quickMode) {
      const enelSole = cantieriList.find(c => c.nome.toLowerCase().includes('enel sole'));
      let initialCantieri = cantieriData;
      if (initialCantieri.length === 0 && enelSole) {
        initialCantieri = [{ cantiere_id: enelSole.id, ore: formData.ore_lavorate }];
      }
      setQuickEntry({ worker, day, form: formData, cantieri: initialCantieri });
    } else {
      setSelectedWorker(worker);
      setSelectedDay(day);
      setEditForm(formData);
      setSelectedCantieri(cantieriData);
      setShowModal(true);
    }
  };

  const addCantiere = () => {
    if (cantieriList.length > 0) {
      setSelectedCantieri([...selectedCantieri, { cantiere_id: cantieriList[0].id, ore: 0 }]);
    }
  };

  const updateCantiereOre = (index, ore) => {
    const updated = [...selectedCantieri];
    updated[index].ore = ore;
    setSelectedCantieri(updated);
  };

  const updateCantiereId = (index, cantiere_id) => {
    const updated = [...selectedCantieri];
    updated[index].cantiere_id = cantiere_id;
    setSelectedCantieri(updated);
  };

  const removeCantiere = (index) => {
    setSelectedCantieri(selectedCantieri.filter((_, i) => i !== index));
  };

  const copyFromPreviousDay = async () => {
    if (!selectedWorker || !selectedDay) return;
    let prevDay = selectedDay - 1;
    let prevMonth = month;
    let prevYear = year;
    if (prevDay < 1) {
      prevMonth = month === 1 ? 12 : month - 1;
      prevYear = month === 1 ? year - 1 : year;
      prevDay = new Date(prevYear, prevMonth, 0).getDate();
      try {
        const dateStr = `${prevYear}-${String(prevMonth).padStart(2,'0')}-${String(prevDay).padStart(2,'0')}`;
        const prev = await apiCall(`/api/presenze/${selectedWorker.id}/${dateStr}`);
        if (prev) {
          setEditForm({
            ore_lavorate: Number(prev.ore_lavorate) || 0,
            ore_straordinario: Number(prev.ore_straordinario) || 0,
            ore_trasferta: Number(prev.ore_trasferta) || 0,
            ore_reperibilita: Number(prev.ore_reperibilita) || 0,
            ore_permesso: Number(prev.ore_permesso) || 0,
            stato: prev.stato || 'Lavorato'
          });
          if (prev.cantieri) setSelectedCantieri(prev.cantieri.map(c => ({ cantiere_id: Number(c.cantiere_id), ore: Number(c.ore) })));
        }
      } catch (err) { console.error(err); }
      return;
    }
    const prev = getAttendanceFor(selectedWorker.id, prevDay);
    if (prev) {
      setEditForm({
        ore_lavorate: Number(prev.ore_lavorate) || 0,
        ore_straordinario: Number(prev.ore_straordinario) || 0,
        ore_trasferta: Number(prev.ore_trasferta) || 0,
        ore_reperibilita: Number(prev.ore_reperibilita) || 0,
        ore_permesso: Number(prev.ore_permesso) || 0,
        stato: prev.stato || 'Lavorato'
      });
      setSelectedCantieri(prev.cantieri ? prev.cantieri.map(c => ({ cantiere_id: c.cantiere_id, ore: c.ore })) : []);
    }
  };

  const saveQuickEntry = async () => {
    if (!quickEntry) return;
    const { worker, day, form, cantieri: qCantieri } = quickEntry;
    const d = `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    try {
      await apiCall('/api/presenze', {
        method: 'POST',
        body: JSON.stringify({
          operaio_id: worker.id,
          data: d,
          ore_lavorate: parseFloat(form.ore_lavorate) || 0,
          ore_straordinario: parseFloat(form.ore_straordinario) || 0,
          ore_trasferta: parseFloat(form.ore_trasferta) || 0,
          ore_reperibilita: parseFloat(form.ore_reperibilita) || 0,
          ore_permesso: parseFloat(form.ore_permesso) || 0,
          stato: form.stato,
          cantieri: qCantieri
        })
      });
      setQuickEntry(null);
      loadData();
    } catch (err) { alert('Errore: ' + err.message); }
  };

  const saveBulkAttendance = async () => {
    if (selectedWorkers.size === 0) return;
    const d = `${year}-${String(month).padStart(2,'0')}-${String(bulkDay).padStart(2,'0')}`;
    try {
      await Promise.all([...selectedWorkers].map(workerId =>
        apiCall('/api/presenze', {
          method: 'POST',
          body: JSON.stringify({
            operaio_id: workerId,
            data: d,
            ore_lavorate: parseFloat(bulkForm.ore_lavorate) || 0,
            ore_straordinario: parseFloat(bulkForm.ore_straordinario) || 0,
            ore_trasferta: parseFloat(bulkForm.ore_trasferta) || 0,
            ore_reperibilita: parseFloat(bulkForm.ore_reperibilita) || 0,
            ore_permesso: parseFloat(bulkForm.ore_permesso) || 0,
            stato: bulkForm.stato,
            cantieri: []
          })
        })
      ));
      setShowBulkPanel(false);
      setSelectedWorkers(new Set());
      loadData();
    } catch (err) { alert('Errore: ' + err.message); }
  };

  const saveAttendance = async () => {
    if (!selectedWorker || !selectedDay) return;
    const d = `${year}-${String(month).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    const dataToSave = {
      operaio_id: selectedWorker.id,
      data: d,
      ore_lavorate: parseFloat(editForm.ore_lavorate) || 0,
      ore_straordinario: parseFloat(editForm.ore_straordinario) || 0,
      ore_trasferta: parseFloat(editForm.ore_trasferta) || 0,
      ore_reperibilita: parseFloat(editForm.ore_reperibilita) || 0,
      ore_permesso: parseFloat(editForm.ore_permesso) || 0,
      stato: editForm.stato,
      cantieri: selectedCantieri.map(c => ({ cantiere_id: Number(c.cantiere_id), ore: Number(c.ore) || 0 }))
    };
    console.log('Saving:', JSON.stringify(dataToSave));
    try {
      const result = await apiCall('/api/presenze', {
        method: 'POST',
        body: JSON.stringify(dataToSave)
      });
      console.log('Save result:', result);
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error('Save error:', err);
      alert('Errore nel salvataggio: ' + err.message);
    }
  };

  const totalDays = getDaysInMonth(month, year);

  return (
    <div className="dashboard">
      <div className="header">
        <h1>{MONTHS[month]} {year}</h1>
        <div className="header-right">
          <button
            className={`quick-mode-btn${quickMode ? ' active' : ''}`}
            onClick={() => setQuickMode(!quickMode)}
            title="Modalità inserimento rapido: mostra un pannello compatto per ogni cella"
          >⚡ {quickMode ? 'Rapido ON' : 'Rapido'}</button>
          <button
            className="btn-secondary btn-small"
            onClick={() => {
              if (selectedWorkers.size === workers.length && workers.length > 0)
                setSelectedWorkers(new Set());
              else
                setSelectedWorkers(new Set(workers.map(w => w.id)));
            }}
          >{selectedWorkers.size > 0 ? `${selectedWorkers.size} sel. ✕` : 'Sel. tutti'}</button>
          {selectedWorkers.size > 0 && (
            <button className="btn-bulk" onClick={() => { setBulkDay(new Date().getDate()); setShowBulkPanel(true); }}>
              Applica a {selectedWorkers.size} operai
            </button>
          )}
          <div className="nav-buttons">
            <button onClick={() => { setMonth(month === 1 ? 12 : month - 1); if (month === 1) setYear(year - 1); }}>&lt;</button>
            <button onClick={() => { setMonth(month === 12 ? 1 : month + 1); if (month === 12) setYear(year + 1); }}>&gt;</button>
          </div>
        </div>
      </div>

      <div className="legend">
        <span className="legend-item"><span className="legend-color legend-lavorate"></span> Ore Lavorate</span>
        <span className="legend-item"><span className="legend-color legend-straordinario"></span> Straordinario</span>
        <span className="legend-item"><span className="legend-color legend-reperibilita"></span> Reperibilità</span>
        <span className="legend-item"><span className="legend-color legend-trasferta"></span> Trasferta</span>
        <span className="legend-item"><span className="legend-color legend-permesso"></span> Permesso</span>
      </div>

      {workers.length === 0 ? (
        <div className="no-workers-box">
          <p>Nessun operaio presente.</p>
          <p>Aggiungi operai dalla sezione Operai.</p>
        </div>
      ) : (
        <div className="workers-list">
          {workers.map(worker => (
            <div key={worker.id} className="worker-row">
              <div className="worker-info">
                <input
                  type="checkbox"
                  className="worker-checkbox"
                  checked={selectedWorkers.has(worker.id)}
                  onChange={e => {
                    const next = new Set(selectedWorkers);
                    if (e.target.checked) next.add(worker.id);
                    else next.delete(worker.id);
                    setSelectedWorkers(next);
                  }}
                  onClick={e => e.stopPropagation()}
                />
                <span className="worker-code">{worker.matricola}</span>
                <span className="worker-name">{worker.nome} {worker.cognome}</span>
              </div>
              <div className="days-grid">
                {[...Array(totalDays)].map((_, i) => {
                  const day = i + 1;
                  const att = getAttendanceFor(worker.id, day);
                  const weekend = isWeekend(day);
                  const holiday = isHoliday(day);
                  let dayClass = 'day-cell';
                  if (weekend) dayClass += ' weekend';
                  if (holiday) dayClass += ' holiday';
                  if (att && att.stato === 'Lavorato') dayClass += ' lavorato';
                  if (att && att.stato === 'TS') dayClass += ' ts';
                  if (att && att.stato === 'R') dayClass += ' r';
                  if (att && att.stato === 'F') dayClass += ' f';
                  if (att && att.stato === 'PIOG') dayClass += ' pioggia';
                  if (att && (att.stato === 'M' || att.stato === 'A' || att.stato === 'P')) dayClass += ' ' + att.stato.toLowerCase();

                  return (
                    <div key={day} className={dayClass} onClick={() => handleDayClick(worker, day)}>
                      <span className="day-label">{getDayName(day)}</span>
                      <span className="day-num">{day}</span>
                      {holiday && <span className="day-holiday">{holiday.name.substring(0, 4)}</span>}
                      {att && att.stato === 'TS' && <span className="day-status">TS</span>}
                      {att && att.stato === 'R' && <span className="day-status">R</span>}
                      {att && att.stato !== 'Lavorato' && att.stato !== 'TS' && att.stato !== 'R' && <span className="day-status">{att.stato}</span>}
                      {att && (att.ore_lavorate > 0 || att.ore_straordinario > 0 || att.ore_reperibilita > 0 || att.ore_trasferta > 0) && (
                        <div className="hours-display">
                          {att.ore_lavorate > 0 && <span className="hour-lavorate">{att.ore_lavorate}</span>}
                          {att.ore_straordinario > 0 && <span className="hour-straordinario">+{att.ore_straordinario}</span>}
                          {att.ore_reperibilita > 0 && <span className="hour-reperibilita">R:{att.ore_reperibilita}</span>}
                          {att.ore_trasferta > 0 && <span className="hour-trasferta">T:{att.ore_trasferta}</span>}
                          {att.ore_permesso > 0 && <span className="hour-permesso">P:{att.ore_permesso}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {quickEntry && (
        <div className="modal-overlay" onClick={() => setQuickEntry(null)}>
          <div className="quick-popover" onClick={e => e.stopPropagation()}>
            <div className="quick-popover-header">
              <span>{quickEntry.worker.nome} {quickEntry.worker.cognome}</span>
              <span className="modal-date">{quickEntry.day} {MONTHS[month]}</span>
            </div>
            <div className="quick-stato-buttons">
              {['Lavorato','R','TS','M','P','F','A','PIOG'].map(s => (
                <button key={s} type="button"
                  className={`stato-btn${quickEntry.form.stato === s ? ' active' : ''}`}
                  onClick={() => setQuickEntry({...quickEntry, form: {...quickEntry.form, stato: s}})}>
                  {s === 'Lavorato' ? 'LAV' : s}
                </button>
              ))}
            </div>
            <OreInput
              label="Ore Lavorate"
              value={quickEntry.form.ore_lavorate}
              onChange={v => {
                const enelSole = cantieriList.find(c => c.nome.toLowerCase().includes('enel sole'));
                const isAutoEnel = enelSole && quickEntry.cantieri.length === 1 && quickEntry.cantieri[0].cantiere_id === enelSole.id;
                setQuickEntry({
                  ...quickEntry,
                  form: {...quickEntry.form, ore_lavorate: v},
                  cantieri: isAutoEnel ? [{ cantiere_id: enelSole.id, ore: v }] : quickEntry.cantieri
                });
              }}
            />
            {(() => {
              const enelSole = cantieriList.find(c => c.nome.toLowerCase().includes('enel sole'));
              if (!enelSole || quickEntry.cantieri.length === 0) return null;
              const isAuto = quickEntry.cantieri.length === 1 && quickEntry.cantieri[0].cantiere_id === enelSole.id;
              const hasCustom = quickEntry.cantieri.length > 0 && !isAuto;
              return (
                <div className="quick-cantiere-info">
                  <span className="quick-cantiere-label">Cantiere</span>
                  <span className={`quick-cantiere-value${hasCustom ? ' custom' : ''}`}>
                    {isAuto ? `Enel Sole • ${quickEntry.cantieri[0].ore}h` : 'personalizzato → Espandi'}
                  </span>
                </div>
              );
            })()}
            <div className="modal-actions" style={{marginTop: '14px'}}>
              <button className="btn-save" onClick={saveQuickEntry}>Salva</button>
              <button className="btn-cancel" onClick={() => {
                setSelectedWorker(quickEntry.worker);
                setSelectedDay(quickEntry.day);
                setEditForm(quickEntry.form);
                setSelectedCantieri(quickEntry.cantieri);
                setQuickEntry(null);
                setShowModal(true);
              }}>Espandi</button>
              <button className="btn-cancel" style={{flex: 'none', padding: '10px 12px'}} onClick={() => setQuickEntry(null)}>✕</button>
            </div>
          </div>
        </div>
      )}

      {showBulkPanel && (
        <div className="bulk-panel">
          <div className="bulk-panel-content">
            <span>{selectedWorkers.size} operai</span>
            <select value={bulkDay} onChange={e => setBulkDay(parseInt(e.target.value))}>
              {[...Array(getDaysInMonth(month, year))].map((_, i) => (
                <option key={i+1} value={i+1}>{i+1} {MONTHS[month]}</option>
              ))}
            </select>
            <select value={bulkForm.stato} onChange={e => setBulkForm({...bulkForm, stato: e.target.value})}>
              <option value="Lavorato">Lavorato</option>
              <option value="R">Reperibilità</option>
              <option value="TS">Turno Sabato</option>
              <option value="M">Malattia</option>
              <option value="P">Permesso</option>
              <option value="F">Ferie</option>
              <option value="A">Assenza</option>
              <option value="PIOG">Pioggia</option>
            </select>
            <div className="bulk-ore-field">
              <span>Ore</span>
              <input
                type="number" step="0.5" min="0" max="24" inputMode="decimal"
                value={bulkForm.ore_lavorate}
                onChange={e => setBulkForm({...bulkForm, ore_lavorate: e.target.value})}
              />
            </div>
            <button className="btn-save" style={{padding:'8px 16px'}} onClick={saveBulkAttendance}>Applica</button>
            <button className="btn-cancel" style={{padding:'8px 12px', flex:'none'}} onClick={() => setShowBulkPanel(false)}>✕</button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <h3>{selectedWorker?.nome} {selectedWorker?.cognome}</h3>
            <p className="modal-date">{selectedDay} {MONTHS[month]} {year}</p>
            <button type="button" className="btn-copy-prev" onClick={copyFromPreviousDay}>
              ← Copia da ieri
            </button>
            <div className="form-fields">
              <div className="form-field">
                <label>Stato</label>
                <select value={editForm.stato} onChange={e => setEditForm({...editForm, stato: e.target.value})}>
                  <option value="Lavorato">Lavorato</option>
                  <option value="R">Reperibilità</option>
                  <option value="TS">Turno Sabato</option>
                  <option value="M">Malattia</option>
                  <option value="P">Permesso</option>
                  <option value="F">Ferie</option>
                  <option value="A">Assenza</option>
                  <option value="PIOG">Pioggia</option>
                </select>
              </div>
              <OreInput label="Ore Lavorate" value={editForm.ore_lavorate} onChange={v => setEditForm({...editForm, ore_lavorate: v})} />
              <OreInput label="Ore Reperibilità" value={editForm.ore_reperibilita} onChange={v => setEditForm({...editForm, ore_reperibilita: v})} />
              <OreInput label="Ore Straordinario" value={editForm.ore_straordinario} onChange={v => setEditForm({...editForm, ore_straordinario: v})} />
              <OreInput label="Ore Trasferta" value={editForm.ore_trasferta} onChange={v => setEditForm({...editForm, ore_trasferta: v})} />
              <OreInput label="Ore Permesso" value={editForm.ore_permesso} onChange={v => setEditForm({...editForm, ore_permesso: v})} />
            </div>
            
            {/* Cantieri Section */}
            <div className="cantieri-section">
              <div className="cantieri-header">
                <span>Cantieri</span>
                <button type="button" className="btn-add-cantiere" onClick={addCantiere}>+ Aggiungi</button>
              </div>
              {selectedCantieri.map((c, index) => (
                <div key={index} className="cantiere-row">
                  <select value={c.cantiere_id} onChange={e => updateCantiereId(index, parseInt(e.target.value))}>
                    {cantieriList.map(cant => (
                      <option key={cant.id} value={cant.id}>{cant.nome}</option>
                    ))}
                  </select>
                  <input type="number" step="0.5" placeholder="Ore" value={c.ore} onChange={e => updateCantiereOre(index, e.target.value)} />
                  <button type="button" className="btn-remove" onClick={() => removeCantiere(index)}>X</button>
                </div>
              ))}
              {selectedCantieri.length > 0 && (
                <button type="button" className="btn-reset" onClick={async () => {
                  if (confirm('Resettare tutti i cantieri per questo giorno?')) {
                    try {
                      await apiCall(`/api/presenze/cantieri?operaio_id=${selectedWorker.id}&data=${year}-${String(month).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`, { method: 'DELETE' });
                      setSelectedCantieri([]);
                      loadData();
                    } catch (err) { console.error(err); }
                  }
                }} style={{marginTop: '8px', fontSize: '12px'}}>Resetta Cantieri</button>
              )}
            </div>
            
            <div className="modal-actions">
              <button className="btn-save" onClick={saveAttendance}>Salva</button>
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Workers({ role }) {
  const [workers, setWorkers] = useState([]);
  const [form, setForm] = useState({ nome: '', cognome: '', matricola: '' });
  const [editing, setEditing] = useState(null);

  useEffect(() => { loadWorkers(); }, []);

  const loadWorkers = async () => {
    try { const w = await apiCall('/api/operai'); setWorkers(w.sort((a, b) => a.id - b.id)); } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await apiCall(`/api/operai/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
      else await apiCall('/api/operai', { method: 'POST', body: JSON.stringify(form) });
      setForm({ nome: '', cognome: '', matricola: '' });
      setEditing(null);
      loadWorkers();
    } catch (err) { alert(err.message); }
  };

  const handleEdit = (w) => { setForm(w); setEditing(w.id); };
  const handleDelete = async (id) => {
    if (window.confirm('Eliminare?')) {
      try { await apiCall(`/api/operai/${id}`, { method: 'DELETE' }); loadWorkers(); } catch (err) { alert(err.message); }
    }
  };

  return (
    <div className="page">
      <h2>Operai</h2>
      <form className="form-box" onSubmit={handleSubmit}>
        <input placeholder="Nome" value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} required />
        <input placeholder="Cognome" value={form.cognome} onChange={e => setForm({...form, cognome: e.target.value})} required />
        <input placeholder="Matricola" value={form.matricola} onChange={e => setForm({...form, matricola: e.target.value})} required />
        <button type="submit">{editing ? 'Modifica' : 'Aggiungi'}</button>
        {editing && <button type="button" onClick={() => { setEditing(null); setForm({ nome: '', cognome: '', matricola: '' }); }}>Annulla</button>}
      </form>
      <table className="data-table">
        <thead><tr><th>Matr</th><th>Nome</th><th>Cognome</th>{role === 'admin' && <th>Azioni</th>}</tr></thead>
        <tbody>
          {workers.map(w => (
            <tr key={w.id}>
              <td><span className="code">{w.matricola}</span></td>
              <td>{w.nome}</td>
              <td>{w.cognome}</td>
              {role === 'admin' && <td><button onClick={() => handleEdit(w)}>Mod</button><button onClick={() => handleDelete(w.id)}>Elim</button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Sites({ role }) {
  const [sites, setSites] = useState([]);
  const [form, setForm] = useState({ nome: '', indirizzo: '', codice: '' });
  const [editing, setEditing] = useState(null);

  useEffect(() => { loadSites(); }, []);

  const loadSites = async () => {
    try { setSites(await apiCall('/api/cantieri')); } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await apiCall(`/api/cantieri/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
      else await apiCall('/api/cantieri', { method: 'POST', body: JSON.stringify(form) });
      setForm({ nome: '', indirizzo: '', codice: '' });
      setEditing(null);
      loadSites();
    } catch (err) { alert(err.message); }
  };

  const handleEdit = (c) => { setForm(c); setEditing(c.id); };
  const handleDelete = async (id) => {
    if (window.confirm('Eliminare?')) {
      try { await apiCall(`/api/cantieri/${id}`, { method: 'DELETE' }); loadSites(); } catch (err) { alert(err.message); }
    }
  };

  return (
    <div className="page">
      <h2>Cantieri</h2>
      <form className="form-box" onSubmit={handleSubmit}>
        <input placeholder="Nome" value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} required />
        <input placeholder="Indirizzo" value={form.indirizzo} onChange={e => setForm({...form, indirizzo: e.target.value})} />
        <input placeholder="Codice" value={form.codice} onChange={e => setForm({...form, codice: e.target.value})} />
        <button type="submit">{editing ? 'Modifica' : 'Aggiungi'}</button>
        {editing && <button type="button" onClick={() => { setEditing(null); setForm({ nome: '', indirizzo: '', codice: '' }); }}>Annulla</button>}
      </form>
      <table className="data-table">
        <thead><tr><th>Codice</th><th>Nome</th><th>Indirizzo</th>{role === 'admin' && <th>Azioni</th>}</tr></thead>
        <tbody>
          {sites.map(s => (
            <tr key={s.id}>
              <td><span className="code">{s.codice}</span></td>
              <td>{s.nome}</td>
              <td>{s.indirizzo}</td>
              {role === 'admin' && <td><button onClick={() => handleEdit(s)}>Mod</button><button onClick={() => handleDelete(s.id)}>Elim</button></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Reports() {
  const [reportType, setReportType] = useState('mensile');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('tabella');
  const [dataInizio, setDataInizio] = useState('');
  const [dataFine, setDataFine] = useState('');
  const festivita = useFestivita(year);

  const loadReport = async () => {
    setLoading(true);
    try {
      let url;
      if (reportType === 'dettaglio-settimana') {
        if (!dataInizio || !dataFine) {
          const today = new Date();
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay() + 1);
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 5);
          setDataInizio(startOfWeek.toISOString().split('T')[0]);
          setDataFine(endOfWeek.toISOString().split('T')[0]);
          return;
        }
        url = `/api/report/dettaglio-settimana?data_inizio=${dataInizio}&data_fine=${dataFine}`;
      } else if (reportType === 'mensile' && viewMode === 'dettaglio') {
        url = `/api/report/mensile-giorni?mese=${month}&anno=${year}`;
      } else {
        url = `/api/report/${reportType}?${reportType === 'mensile' ? `mese=${month}&` : ''}anno=${year}`;
      }
      setData(await apiCall(url));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  useEffect(() => { 
    loadReport();
  }, [viewMode, reportType, month, year, dataInizio, dataFine]);

  const getDaysInMonth = () => new Date(year, month, 0).getDate();
  const totalDays = getDaysInMonth();

const exportExcel = () => {
      try {
        const XLSX = require('xlsx');
        const XLSXSTYLE = require('xlsx-js-style');

        const thickBorder = { style: 'medium', color: { rgb: '000000' } };
        const thinBorder = { style: 'thin', color: { rgb: '000000' } };
        const fullThinBorders = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
        const cantiereCellBorders = (R, C, lastCol, block) => ({
          top: R === block.startRow ? thickBorder : thinBorder,
          bottom: R === block.endRow ? thickBorder : thinBorder,
          left: C === 0 ? thickBorder : thinBorder,
          right: C === lastCol ? thickBorder : thinBorder
        });
        const computeAutoWidths = (rows, merges, lastCol, minWidth = 4, padding = 2) => {
          const widths = new Array(lastCol + 1).fill(minWidth);
          for (let R = 0; R < rows.length; R++) {
            for (let C = 0; C <= lastCol; C++) {
              const wideMergeAtCell = merges.find(m => m.s.r === R && m.s.c === C && m.e.c > m.s.c);
              if (wideMergeAtCell) continue;
              const insideMerge = merges.some(m =>
                !(m.s.r === R && m.s.c === C) &&
                R >= m.s.r && R <= m.e.r &&
                C >= m.s.c && C <= m.e.c
              );
              if (insideMerge) continue;
              const cellVal = rows[R] && rows[R][C] != null ? String(rows[R][C]) : '';
              if (cellVal.length > widths[C]) widths[C] = cellVal.length;
            }
          }
          return widths.map(w => ({ wch: w + padding }));
        };

        if (reportType === 'dettaglio-settimana') {
          const reportData = data;
          const giorni = reportData.giorni || [];
          const cantieriList = reportData.cantieri || [];
          let operaiList = reportData.operai || [];
          operaiList.sort((a, b) => Number(a.matricola) - Number(b.matricola));

          if (!operaiList || operaiList.length === 0) {
            alert('Nessun dato da esportare');
            return;
          }
        
        const wb = XLSX.utils.book_new();

        // ----------------- GENERAZIONE FOGLIO DI RIEPILOGO -----------------
        const summaryRows = [];
        const summaryMerges = [];
        const summaryBannerRowIndices = new Set();
        const summaryCantiereBlocks = [];
        
        // Riga 0: Mese
        const firstDay = new Date(giorni[0]);
        const summaryRow0 = ['Mese ' + MONTHS[firstDay.getMonth() + 1] + ' ' + firstDay.getFullYear()];
        while (summaryRow0.length <= giorni.length) summaryRow0.push('');
        summaryRows.push(summaryRow0);
        summaryMerges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: giorni.length } });
        
        // Riga 1: Intestazione Colonne/Date
        const summaryDataRow = ['OPERAIO / CANTIERE'];
        giorni.forEach(g => {
          const dateObj = new Date(g);
          const gg = String(dateObj.getDate()).padStart(2, '0');
          const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
          const aa = String(dateObj.getFullYear()).slice(-2);
          summaryDataRow.push(`${gg}/${mm}/${aa}`);
        });
        summaryRows.push(summaryDataRow);
        
        let currentSummaryRowIdx = 2;
        
        operaiList.forEach(worker => {
          // Riga Banner Operaio
          const bannerRow = [worker.cognome + ' ' + worker.nome + ' (Matr. ' + worker.matricola + ')'];
          while (bannerRow.length <= giorni.length) bannerRow.push('');
          summaryRows.push(bannerRow);
          summaryMerges.push({ s: { r: currentSummaryRowIdx, c: 0 }, e: { r: currentSummaryRowIdx, c: giorni.length } });
          summaryBannerRowIndices.add(currentSummaryRowIdx);
          currentSummaryRowIdx++;
          
          const workerCantieri = [];
          if (worker.giorni) {
            worker.giorni.forEach(g => {
              if (g.cantieri) {
                g.cantieri.forEach(c => {
                  if (Number(c.ore) > 0) {
                    const cantObject = cantieriList.find(x => x.id === c.cantiere_id);
                    if (cantObject) {
                      if (!workerCantieri.some(x => x.id === cantObject.id)) {
                        workerCantieri.push(cantObject);
                      }
                    } else {
                      if (!workerCantieri.some(x => x.id === c.cantiere_id)) {
                        workerCantieri.push({ id: c.cantiere_id, nome: c.nome || `Cantiere #${c.cantiere_id}` });
                      }
                    }
                  }
                });
              }
            });
          }
          const cantieriToIterate = workerCantieri.length > 0 ? workerCantieri : [{ id: null, nome: 'Nessun Cantiere' }];
          
          const ownsDay = (g, cant) => ownsDayCommon(g, cant, cantieriToIterate[0].id);
          
          cantieriToIterate.forEach((cant) => {
            let blockRows = [];
            const titleRow = [cant.nome];
            const oreValRow = [''];
            
            giorni.forEach((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              const cantData = g && g.cantieri && g.cantieri.find(x => x.cantiere_id === cant.id);
              const oreValue = cantData && cantData.ore ? cantData.ore : '';
              const hasOre = oreValue !== '' && Number(oreValue) > 0;

              if (g && ['M', 'P', 'F', 'A', 'PIOG', 'TS'].includes(g.stato) && ownsDay(g, cant)) {
                const statoLabels = { 'M': 'M', 'P': 'PERM', 'F': 'F', 'A': 'A', 'PIOG': 'PIOG', 'TS': 'TS' };
                titleRow.push(statoLabels[g.stato] || g.stato);
                oreValRow.push(oreValue);
              } else if (hasOre) {
                titleRow.push('LAV');
                oreValRow.push(oreValue);
              } else {
                titleRow.push('');
                oreValRow.push('');
              }
            });
            blockRows.push(titleRow);
            blockRows.push(oreValRow);

            const hasTrasferta = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_trasferta > 0 && ownsDay(g, cant);
            });
            if (hasTrasferta) {
              const trLabelRow = [''];
              const trValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_trasferta > 0 && ownsDay(g, cant)) {
                  trLabelRow.push('TRASF');
                  trValRow.push(g.ore_trasferta);
                } else {
                  trLabelRow.push('');
                  trValRow.push('');
                }
              });
              blockRows.push(trLabelRow);
              blockRows.push(trValRow);
            }

            const hasStraord = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_straordinario > 0 && ownsDay(g, cant);
            });
            if (hasStraord) {
              const stLabelRow = [''];
              const stValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_straordinario > 0 && ownsDay(g, cant)) {
                  stLabelRow.push('STRAO');
                  stValRow.push(g.ore_straordinario);
                } else {
                  stLabelRow.push('');
                  stValRow.push('');
                }
              });
              blockRows.push(stLabelRow);
              blockRows.push(stValRow);
            }

            const hasReperib = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_reperibilita > 0 && ownsDay(g, cant);
            });
            if (hasReperib) {
              const repLabelRow = [''];
              const repValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_reperibilita > 0 && ownsDay(g, cant)) {
                  repLabelRow.push('REP');
                  repValRow.push(g.ore_reperibilita);
                } else {
                  repLabelRow.push('');
                  repValRow.push('');
                }
              });
              blockRows.push(repLabelRow);
              blockRows.push(repValRow);
            }

            const hasPermesso = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_permesso > 0 && ownsDay(g, cant);
            });
            if (hasPermesso) {
              const perLabelRow = [''];
              const perValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_permesso > 0 && ownsDay(g, cant)) {
                  perLabelRow.push('PERM');
                  perValRow.push(g.ore_permesso);
                } else {
                  perLabelRow.push('');
                  perValRow.push('');
                }
              });
              blockRows.push(perLabelRow);
              blockRows.push(perValRow);
            }

            const blockStartRow = currentSummaryRowIdx;
            const blockEndRow = currentSummaryRowIdx + blockRows.length - 1;
            summaryRows.push(...blockRows);

            if (blockRows.length > 1) {
              summaryMerges.push({
                s: { r: blockStartRow, c: 0 },
                e: { r: blockEndRow, c: 0 }
              });
            }
            summaryCantiereBlocks.push({ startRow: blockStartRow, endRow: blockEndRow });

            currentSummaryRowIdx += blockRows.length;
          });

          const hasRepStato = worker.giorni && worker.giorni.some(g => g && g.stato === 'R');
          if (hasRepStato) {
            const repRow = ['REP'];
            giorni.forEach((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              repRow.push(g && g.stato === 'R' ? 'REP' : '');
            });
            summaryRows.push(repRow);
            currentSummaryRowIdx++;
          }
        });

        const ws_summary = XLSX.utils.aoa_to_sheet(summaryRows);
        ws_summary['!merges'] = summaryMerges;

        // Applicazione stili al riepilogo
        const greyFill = { patternType: 'solid', fgColor: { rgb: 'C0C0C0' }, bgColor: { rgb: 'FFFFFF' } };
        const bannerFill = { patternType: 'solid', fgColor: { rgb: 'E3F2FD' }, bgColor: { rgb: 'FFFFFF' } }; // Azzurro chiaro
        const findSummaryBlock = (R) => summaryCantiereBlocks.find(b => R >= b.startRow && R <= b.endRow);

        for (let R = 0; R < summaryRows.length; ++R) {
          const block = findSummaryBlock(R);
          for (let C = 0; C <= giorni.length; ++C) {
            const cellAddress = { c: C, r: R };
            const cellRef = XLSX.utils.encode_cell(cellAddress);
            if (!ws_summary[cellRef]) {
              ws_summary[cellRef] = { t: 's', v: '' };
            }

            let cellStyle = {
              border: block ? cantiereCellBorders(R, C, giorni.length, block) : fullThinBorders,
              alignment: { vertical: 'center', horizontal: 'center', wrapText: true }
            };

            const currentVal = ws_summary[cellRef].v;
            const cellAboveRef = R > 0 ? XLSX.utils.encode_cell({ c: C, r: R - 1 }) : null;
            const valAbove = cellAboveRef && ws_summary[cellAboveRef] ? ws_summary[cellAboveRef].v : '';

            if (R === 0) {
              cellStyle.fill = greyFill;
              cellStyle.font = { bold: true };
            } else if (R === 1) {
              cellStyle.fill = greyFill;
              cellStyle.font = { bold: true };
            } else if (summaryBannerRowIndices.has(R)) {
              cellStyle.fill = bannerFill;
              cellStyle.font = { bold: true };
              cellStyle.alignment = { vertical: 'center', horizontal: 'left', wrapText: true };
            } else if (C === 0) {
              cellStyle.fill = greyFill;
              cellStyle.font = { bold: true };
            } else {
              if (isRedState(currentVal) || isRedState(valAbove)) {
                cellStyle.fill = { patternType: 'solid', fgColor: { rgb: 'FFCDD2' } };
              } else if (isBlueState(currentVal) || isBlueState(valAbove)) {
                cellStyle.fill = { patternType: 'solid', fgColor: { rgb: 'B3E5FC' } };
              }

              const shouldBold = block ? (R - block.startRow) % 2 === 0 : true;
              if (shouldBold && currentVal !== '') {
                if (!cellStyle.font) cellStyle.font = {};
                cellStyle.font.bold = true;
              }
            }

            ws_summary[cellRef].s = cellStyle;
          }
        }

        ws_summary['!cols'] = computeAutoWidths(summaryRows, summaryMerges, giorni.length);

        ws_summary['!rows'] = [];
        for (let r = 0; r < summaryRows.length; r++) {
          ws_summary['!rows'][r] = { h: 20 };
        }

        XLSX.utils.book_append_sheet(wb, ws_summary, 'Riepilogo');

        // ----------------- GENERAZIONE FOGLI SINGOLI -----------------
        operaiList.forEach(worker => {
          const rows = [];
          const merges = [];
          const workerCantiereBlocks = [];
          
          // Row 1: Name + Matricola
          const row1 = [worker.cognome + ' ' + worker.nome];
          while (row1.length < 2) row1.push(''); // C1 will be Matr.
          row1.push('Matr. ' + worker.matricola);
          while (row1.length <= giorni.length) row1.push('');
          rows.push(row1);
          merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });
          
          // Row 2: Month
          const firstDay = new Date(giorni[0]);
          const row2 = ['Mese ' + MONTHS[firstDay.getMonth() + 1] + ' ' + firstDay.getFullYear()];
          while(row2.length <= giorni.length) row2.push('');
          rows.push(row2);
          
          // Row 3: DATA header
          const dataRow = ['DATA'];
          giorni.forEach(g => {
            const dateObj = new Date(g);
            const gg = String(dateObj.getDate()).padStart(2, '0');
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const aa = String(dateObj.getFullYear()).slice(-2);
            dataRow.push(`${gg}/${mm}/${aa}`);
          });
          rows.push(dataRow);
          
          // Start dynamic blocks
          let currentRowIdx = 3; 
          
          const workerCantieri = [];
          if (worker.giorni) {
            worker.giorni.forEach(g => {
              if (g.cantieri) {
                g.cantieri.forEach(c => {
                  if (Number(c.ore) > 0) {
                    const cantObject = cantieriList.find(x => x.id === c.cantiere_id);
                    if (cantObject) {
                      if (!workerCantieri.some(x => x.id === cantObject.id)) {
                        workerCantieri.push(cantObject);
                      }
                    } else {
                      if (!workerCantieri.some(x => x.id === c.cantiere_id)) {
                        workerCantieri.push({ id: c.cantiere_id, nome: c.nome || `Cantiere #${c.cantiere_id}` });
                      }
                    }
                  }
                });
              }
            });
          }
          const cantieriToIterate = workerCantieri.length > 0 ? workerCantieri : [{ id: null, nome: 'Nessun Cantiere' }];
          const ownsDay = (g, cant) => ownsDayCommon(g, cant, cantieriToIterate[0].id);
          
          cantieriToIterate.forEach((cant) => {
            let blockRows = [];
            
            // We always have at least the "Lavorate" / "Stato" pair
            const titleRow = [cant.nome];
            const oreValRow = [''];
            
            giorni.forEach((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              const cantData = g && g.cantieri && g.cantieri.find(x => x.cantiere_id === cant.id);
              const oreValue = cantData && cantData.ore ? cantData.ore : '';
              const hasOre = oreValue !== '' && Number(oreValue) > 0;

              if (g && ['M', 'P', 'F', 'A', 'PIOG', 'TS'].includes(g.stato) && ownsDay(g, cant)) {
                const statoLabels = { 'M': 'M', 'P': 'PERM', 'F': 'F', 'A': 'A', 'PIOG': 'PIOG', 'TS': 'TS' };
                titleRow.push(statoLabels[g.stato] || g.stato);
                oreValRow.push(oreValue);
              } else if (hasOre) {
                titleRow.push('LAV');
                oreValRow.push(oreValue);
              } else {
                titleRow.push('');
                oreValRow.push('');
              }
            });
            blockRows.push(titleRow);
            blockRows.push(oreValRow);

            // Appending Trasferta, Straord, Reperib ONLY if this cantiere "owns" those hours
            const hasTrasferta = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_trasferta > 0 && ownsDay(g, cant);
            });
            if (hasTrasferta) {
              const trLabelRow = [''];
              const trValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_trasferta > 0 && ownsDay(g, cant)) {
                  trLabelRow.push('TRASF');
                  trValRow.push(g.ore_trasferta);
                } else {
                  trLabelRow.push('');
                  trValRow.push('');
                }
              });
              blockRows.push(trLabelRow);
              blockRows.push(trValRow);
            }

            const hasStraord = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_straordinario > 0 && ownsDay(g, cant);
            });
            if (hasStraord) {
              const stLabelRow = [''];
              const stValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_straordinario > 0 && ownsDay(g, cant)) {
                  stLabelRow.push('STRAO');
                  stValRow.push(g.ore_straordinario);
                } else {
                  stLabelRow.push('');
                  stValRow.push('');
                }
              });
              blockRows.push(stLabelRow);
              blockRows.push(stValRow);
            }

            const hasReperib = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_reperibilita > 0 && ownsDay(g, cant);
            });
            if (hasReperib) {
              const repLabelRow = [''];
              const repValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_reperibilita > 0 && ownsDay(g, cant)) {
                  repLabelRow.push('REP');
                  repValRow.push(g.ore_reperibilita);
                } else {
                  repLabelRow.push('');
                  repValRow.push('');
                }
              });
              blockRows.push(repLabelRow);
              blockRows.push(repValRow);
            }

            const hasPermesso = giorni.some((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              return g && g.ore_permesso > 0 && ownsDay(g, cant);
            });
            if (hasPermesso) {
              const perLabelRow = [''];
              const perValRow = [''];
              giorni.forEach((_, idx) => {
                const g = worker.giorni && worker.giorni[idx];
                if (g && g.ore_permesso > 0 && ownsDay(g, cant)) {
                  perLabelRow.push('PERM');
                  perValRow.push(g.ore_permesso);
                } else {
                  perLabelRow.push('');
                  perValRow.push('');
                }
              });
              blockRows.push(perLabelRow);
              blockRows.push(perValRow);
            }

            const blockStartRow = currentRowIdx;
            const blockEndRow = currentRowIdx + blockRows.length - 1;
            rows.push(...blockRows);

            if (blockRows.length > 1) {
              merges.push({
                s: { r: blockStartRow, c: 0 },
                e: { r: blockEndRow, c: 0 }
              });
            }
            workerCantiereBlocks.push({ startRow: blockStartRow, endRow: blockEndRow });
            currentRowIdx += blockRows.length;
          });

          const hasRepStato = worker.giorni && worker.giorni.some(g => g && g.stato === 'R');
          if (hasRepStato) {
            const repRow = ['REP'];
            giorni.forEach((_, idx) => {
              const g = worker.giorni && worker.giorni[idx];
              repRow.push(g && g.stato === 'R' ? 'REP' : '');
            });
            rows.push(repRow);
            currentRowIdx++;
          }

          const ws = XLSX.utils.aoa_to_sheet(rows);
          ws['!merges'] = merges;

          // Apply styles
          const greyFill = { patternType: 'solid', fgColor: { rgb: 'C0C0C0' }, bgColor: { rgb: 'FFFFFF' } };
          const findWorkerBlock = (R) => workerCantiereBlocks.find(b => R >= b.startRow && R <= b.endRow);

          for (let R = 0; R < rows.length; ++R) {
            const block = findWorkerBlock(R);
            for (let C = 0; C <= giorni.length; ++C) {
              const cellAddress = { c: C, r: R };
              const cellRef = XLSX.utils.encode_cell(cellAddress);
              if (!ws[cellRef]) {
                ws[cellRef] = { t: 's', v: '' };
              }

              let cellStyle = {
                border: block ? cantiereCellBorders(R, C, giorni.length, block) : fullThinBorders,
                alignment: { vertical: 'center', horizontal: 'center', wrapText: true }
              };

              const currentVal = ws[cellRef].v;
              const cellAboveRef = R > 0 ? XLSX.utils.encode_cell({ c: C, r: R - 1 }) : null;
              const valAbove = cellAboveRef && ws[cellAboveRef] ? ws[cellAboveRef].v : '';

              if (R === 0 && (C === 0 || C === 2)) {
                cellStyle.fill = greyFill;
                cellStyle.font = { bold: true };
              } else if (R === 1 && C === 0) {
                cellStyle.font = { bold: true };
              } else if (R === 2) {
                cellStyle.fill = greyFill;
                cellStyle.font = { bold: true };
              } else if (R >= 3 && C === 0) {
                cellStyle.fill = greyFill;
                cellStyle.font = { bold: true };
              } else if (R >= 3 && C > 0) {
                if (isRedState(currentVal) || isRedState(valAbove)) {
                  cellStyle.fill = { patternType: 'solid', fgColor: { rgb: 'FFCDD2' } };
                } else if (isBlueState(currentVal) || isBlueState(valAbove)) {
                  cellStyle.fill = { patternType: 'solid', fgColor: { rgb: 'B3E5FC' } };
                }

                const shouldBold = block ? (R - block.startRow) % 2 === 0 : true;
                if (shouldBold && currentVal !== '') {
                  if (!cellStyle.font) cellStyle.font = {};
                  cellStyle.font.bold = true;
                }
              }

              ws[cellRef].s = cellStyle;
            }
          }

          ws['!cols'] = computeAutoWidths(rows, merges, giorni.length);

          ws['!rows'] = [];
          for (let r = 0; r < rows.length; r++) {
            ws['!rows'][r] = { h: 20 };
          }

          XLSX.utils.book_append_sheet(wb, ws, (worker.cognome + ' ' + worker.nome).substring(0, 30));
        });
        
        XLSXSTYLE.writeFile(wb, 'report_dettaglio_' + dataInizio + '_' + dataFine + '.xlsx');
        alert('Excel dettaglio settimana esportato!');
        return;
        }
      
      if (viewMode === 'dettaglio' && reportType === 'mensile') {
        const wb = XLSX.utils.book_new();
        
        data.forEach(worker => {
          const rows = [
            [`${worker.cognome} ${worker.nome}`, '', '', '', '', `Matricola: ${worker.matricola}`],
            ['', '', '', '', '', ''],
            ['Giorno', 'LAV', 'STRAO', 'REP', 'TRASF', 'PERM', 'Stato', 'Festivo']
          ];
          
          for (let d = 1; d <= totalDays; d++) {
            const g = worker.giorni && worker.giorni[d - 1];
            if (g && (g.ore_lavorate > 0 || g.ore_straordinario > 0 || g.ore_trasferta > 0 || g.ore_reperibilita > 0)) {
              rows.push([
                d,
                g.ore_lavorate || '',
                g.ore_straordinario || '',
                g.ore_reperibilita || '',
                g.ore_trasferta || '',
                g.ore_permesso || '',
                g.stato || '',
                festivita.find(h => h.date === `${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`) ? 'FESTIVO' : ''
              ]);
            }
          }
          
          const totL = worker.giorni ? worker.giorni.reduce((s, g) => s + (g.ore_lavorate || 0), 0) : 0;
          const totS = worker.giorni ? worker.giorni.reduce((s, g) => s + (g.ore_straordinario || 0), 0) : 0;
          const totR = worker.giorni ? worker.giorni.reduce((s, g) => s + (g.ore_reperibilita || 0), 0) : 0;
          const totT = worker.giorni ? worker.giorni.reduce((s, g) => s + (g.ore_trasferta || 0), 0) : 0;
          const totP = worker.giorni ? worker.giorni.reduce((s, g) => s + (g.ore_permesso || 0), 0) : 0;
          
          rows.push(['', '', '', '', '', '', '', '']);
          rows.push(['TOTALE', totL, totS, totR, totT, totP, '', '']);

          const ws = XLSX.utils.aoa_to_sheet(rows);
          ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 7 } }];

          const colWidths = [{ wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 10 }];
          ws['!cols'] = colWidths;
          
          XLSX.utils.book_append_sheet(wb, ws, (worker.cognome + ' ' + worker.nome).substring(0, 30));
        });
        
        XLSX.writeFile(wb, 'report_dettaglio_' + year + '_' + month + '.xlsx');
        alert('Excel dettaglio esportato!');
      } else {
        const allCantieri = data.length > 0 ? data.flatMap(r => r.cantieri || []).filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i).sort((a, b) => a.id - b.id) : [];
        
        const headerRow = ['Matricola', 'Nome', 'Cognome', 'LAV', 'STRAO', 'TRASF', 'REP', 'PERM', 'R', 'M', 'P', 'F', 'TS', 'A', 'PIOG'];
        allCantieri.forEach(c => headerRow.push(`${c.codice} - ${c.nome}`));
        
        const rows = [['', '', '', '', '', '', '', '', '', '', '', '', '', '', ''], headerRow];
        
        data.forEach(r => {
          const row = [
            r.matricola,
            r.nome,
            r.cognome,
            r.totale_ore,
            r.totale_straordinario,
            r.totale_trasferta,
            r.totale_reperibilita,
            r.totale_permesso,
            r.giorni_reperibile,
            r.giorni_malattia,
            r.giorni_permesso,
            r.giorni_ferie,
            r.ore_turno_sabato,
            r.giorni_assenza,
            r.giorni_pioggia
          ];
          allCantieri.forEach(c => {
            const workerCantiere = r.cantieri && r.cantieri.find(cc => cc.id === c.id);
            row.push(workerCantiere ? workerCantiere.ore : '');
          });
          rows.push(row);
        });
        
        rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
        rows.push(['', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
        
        const ws = XLSX.utils.aoa_to_sheet(rows);
        
        const colWidths = [
          { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, 
          { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, 
          { wch: 10 }, { wch: 12 }, { wch: 10 }, { wch: 10 }, ...allCantieri.map(() => ({ wch: 10 }))
        ];
        ws['!cols'] = colWidths;
        
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Report');
        XLSX.writeFile(wb, 'report_' + reportType + '_' + year + (month ? '_' + month : '') + '.xlsx');
alert('Excel riepilogo esportato!');
      }
    } catch (err) { console.error(err); alert('Errore esportazione Excel: ' + err.message); }
  };

  const allCantieri = data.length > 0 ? data.flatMap(r => r.cantieri || []).filter((c, i, arr) => arr.findIndex(x => x.id === c.id) === i).sort((a, b) => a.id - b.id) : [];

  const exportPDF = () => {
    try {
      const isDettaglio = viewMode === 'dettaglio' && reportType === 'mensile';
      const printContent = document.querySelector('.workers-list') || document.querySelector('.data-table');
      if (!printContent) return;
      const win = window.open('', '', 'height=800,width=1400');
      if (!win) { alert('Blocca popup per stampare'); return; }
      win.document.write('<html><head><title>Report</title>');
      win.document.write('<style>');
      win.document.write('body { font-family: Arial, sans-serif; padding: 15px; background: #fff; color: #000; font-size: 10px; }');
      win.document.write('h1, h2 { text-align: center; margin-bottom: 10px; }');
      win.document.write('table { border-collapse: collapse; width: 100%; font-size: 9px; margin-bottom: 20px; }');
      win.document.write('th, td { border: 1px solid #000; padding: 3px; text-align: center; }');
      win.document.write('th { background: #e0e0e0; font-weight: bold; }');
      win.document.write('.worker-row { margin-bottom: 15px; page-break-inside: avoid; border: 1px solid #999; }');
      win.document.write('.worker-info { background: #f5f5f5; padding: 5px; font-weight: bold; border-bottom: 1px solid #000; }');
      win.document.write('.days-grid { display: flex; flex-wrap: wrap; padding: 5px; }');
      win.document.write('.day-cell { display: inline-block; min-width: 25px; padding: 2px; border: 1px solid #ccc; margin: 1px; text-align: center; font-size: 8px; }');
      win.document.write('.day-label { display: block; font-weight: bold; }');
      win.document.write('.day-num { font-size: 12px; font-weight: 500; display: block; }');
      win.document.write('.hours-display { display: flex; flex-wrap: wrap; justify-content: center; gap: 2px; }');
      win.document.write('.hour-lavorate { color: #2e7d32; font-weight: bold; }');
      win.document.write('.hour-straordinario { color: #e65100; font-weight: bold; }');
      win.document.write('.hour-reperibilita { color: #f9a825; font-weight: bold; }');
      win.document.write('.hour-trasferta { color: #1565c0; font-weight: bold; }');
      win.document.write('.hour-permesso { color: #ba68c8; font-weight: bold; }');
      win.document.write('.day-status { font-weight: bold; }');
      win.document.write('.day-cell.r { background: rgba(255,183,77,0.4); }');
      win.document.write('.day-cell.r .day-status { color: #e65100; }');
      win.document.write('.day-cell.pioggia { background: rgba(33,150,243,0.3); }');
      win.document.write('.day-cell.pioggia .day-status { color: #1565c0; }');
      win.document.write('.day-cell.m, .day-cell.a, .day-cell.p { background: linear-gradient(135deg, rgba(244,67,54,0.3), rgba(244,67,54,0.15)); }');
      win.document.write('.day-cell.m .day-status, .day-cell.a .day-status, .day-cell.p .day-status { color: #c62828; }');
      win.document.write('.day-cell.ts { background: rgba(239,83,80,0.3); }');
      win.document.write('.day-cell.ts .day-status { color: #c62828; }');
      win.document.write('.day-cell.f { background: rgba(76,175,80,0.3); }');
      win.document.write('.day-cell.f .day-status { color: #2e7d32; }');
      win.document.write('.day-cell.holiday { background: rgba(244,67,54,0.3); }');
      win.document.write('.day-cell.weekend { background: rgba(239,83,80,0.15); }');
      win.document.write('.day-cell.weekend .day-label, .day-cell.weekend .day-num { color: #ef5350; }');
      win.document.write('.day-holiday { color: #c62828; font-size: 7px; font-weight: 600; }');
      win.document.write('.legend { margin: 10px 0; padding: 10px; background: #f5f5f5; border: 1px solid #ccc; }');
      win.document.write('.legend-item { display: inline-block; margin-right: 15px; }');
      win.document.write('.legend-color { display: inline-block; width: 15px; height: 15px; margin-right: 5px; vertical-align: middle; }');
      win.document.write('</style>');
      win.document.write('</head><body>');
      win.document.write(`<h2>Report ${reportType === 'mensile' ? 'Mensile' : 'Annuale'} - ${month ? MONTHS[parseInt(month)] + ' ' : ''}${year}</h2>`);
      
      if (isDettaglio) {
        win.document.write('<div class="legend">');
        win.document.write('<strong>Legenda: </strong>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#4caf50"></span>L = Ore Lavorate</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#ff9800"></span>S = Straordinario</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#ffc107"></span>R = Reperibilità</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#2196f3"></span>T = Trasferta</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#ba68c8"></span>P = Ore Permesso</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#4caf50"></span>F = Ferie</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#f44336"></span>M = Malattia</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#f44336"></span>P = Permesso</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#f44336"></span>A = Assenza</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#2196f3"></span>PIOG = Pioggia</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#ef5350"></span>TS = Turno Sabato</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#f44336"></span>Festività</span>');
        win.document.write('<span class="legend-item"><span class="legend-color" style="background:#ef5350"></span>Weekend</span>');
        win.document.write('</div>');
      }
      
      win.document.write(printContent.innerHTML);
      win.document.write('</body></html>');
      win.document.close();
      setTimeout(() => { win.focus(); win.print(); }, 500);
    } catch (err) { console.error(err); alert('Errore esportazione PDF'); }
  };

  return (
    <div className="page">
      <h2>Report</h2>
      <div className="filters">
        <select value={reportType} onChange={e => setReportType(e.target.value)}>
          <option value="mensile">Mensile</option>
          <option value="annuale">Annuale</option>
          <option value="dettaglio-settimana">Dettaglio Giorni (Settimana)</option>
        </select>
        {reportType === 'mensile' && <input type="number" min="1" max="12" value={month} onChange={e => setMonth(e.target.value)} />}
        {reportType === 'dettaglio-settimana' && (
          <>
            <input type="date" value={dataInizio} onChange={e => setDataInizio(e.target.value)} />
            <input type="date" value={dataFine} onChange={e => setDataFine(e.target.value)} />
          </>
        )}
        <input type="number" value={year} onChange={e => setYear(e.target.value)} />
        <button onClick={exportExcel}>Excel</button>
        <button onClick={exportPDF}>PDF</button>
        {reportType === 'mensile' && (
          <select value={viewMode} onChange={e => setViewMode(e.target.value)} style={{marginLeft: '8px'}}>
            <option value="tabella">Riepilogo</option>
            <option value="dettaglio">Dettaglio Giorni</option>
          </select>
        )}
      </div>
      {loading ? <div className="loading">Caricamento...</div> : (
        reportType === 'dettaglio-settimana' && data.operai ? (
          <div className="workers-list">
            {data.operai.map(worker => (
              <div key={worker.id} className="worker-row">
                <div className="worker-info">
                  <span className="worker-code">{worker.matricola}</span>
                  <span className="worker-name">{worker.nome} {worker.cognome}</span>
                </div>
                <div className="days-grid">
                  {worker.giorni.map((g, i) => {
                    const dayDate = new Date(g.data);
                    const dayName = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'][dayDate.getDay()];
                    const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
                    const dayStr = g.data ? g.data.substring(5, 10) : '';
                    const holiday = festivita.find(h => h.date === dayStr);
                    const dayClass = 'day-cell' + (isWeekend ? ' weekend' : '') + (holiday ? ' holiday' : '') + (g.stato === 'Lavorato' ? ' lavorato' : g.stato === 'F' ? ' f' : g.stato === 'R' ? ' r' : g.stato === 'PIOG' ? ' pioggia' : g.stato === 'M' || g.stato === 'A' || g.stato === 'P' ? ' ' + g.stato.toLowerCase() : '');
                    return (
                      <div key={i} className={dayClass}>
                        <span className="day-label">{dayName}</span>
                        <span className="day-num">{dayDate.getDate()}</span>
                        {holiday && <span className="day-holiday">{holiday.name.substring(0, 4)}</span>}
                        {g.stato && <span className="day-status">{g.stato}</span>}
                        {g.ore_lavorate > 0 && <span className="hour-lavorate">{g.ore_lavorate}</span>}
                        {g.ore_straordinario > 0 && <span className="hour-straordinario">+{g.ore_straordinario}</span>}
                        {g.ore_reperibilita > 0 && <span className="hour-reperibilita">R:{g.ore_reperibilita}</span>}
                        {g.ore_trasferta > 0 && <span className="hour-trasferta">T:{g.ore_trasferta}</span>}
                        {g.ore_permesso > 0 && <span className="hour-permesso">P:{g.ore_permesso}</span>}
                        {g.cantieri && g.cantieri.length > 0 && (
                          <div className="hours-display">
                            {g.cantieri.map((c, ci) => (
                              <span key={ci} className="hour-cantiere">{c.codice}:{c.ore}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : viewMode === 'dettaglio' && reportType === 'mensile' ? (
          <div className="workers-list">
            {data.map(worker => (
              <div key={worker.id} className="worker-row">
                <div className="worker-info">
                  <span className="worker-code">{worker.matricola}</span>
                  <span className="worker-name">{worker.nome} {worker.cognome}</span>
                </div>
                <div className="days-grid">
                  {[...Array(totalDays)].map((_, i) => {
                    const day = i + 1;
                    const dayStr = `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const holiday = festivita.find(h => h.date === dayStr);
                    const isWeekend = new Date(year, month - 1, day).getDay() === 0 || new Date(year, month - 1, day).getDay() === 6;
                    const g = worker.giorni && worker.giorni[day - 1];
                    const hasData = g && (g.ore_lavorate > 0 || g.ore_straordinario > 0 || g.ore_trasferta > 0 || g.ore_reperibilita > 0 || g.ore_permesso > 0 || g.stato);
                    const att = hasData ? g : null;
                    let dayClass = 'day-cell';
                    if (holiday) dayClass += ' holiday';
                    else if (isWeekend) dayClass += ' weekend';
                    if (att) {
                      if (att.stato === 'Lavorato') dayClass += ' lavorato';
                      else if (att.stato === 'R') dayClass += ' r';
                      else if (att.stato === 'F') dayClass += ' f';
                      else if (att.stato === 'PIOG') dayClass += ' pioggia';
                      else if (att.stato === 'M' || att.stato === 'A' || att.stato === 'P') dayClass += ' ' + att.stato.toLowerCase();
                    }
                    
                    return (
                      <div key={day} className={dayClass}>
                        <span className="day-label">{DAYS_OF_WEEK[new Date(year, month - 1, day).getDay()]}</span>
                        <span className="day-num">{day}</span>
                        {holiday && !att && <span className="day-holiday">{holiday.name.substring(0, 4)}</span>}
                        {att && (
                          <>
                            {att.stato && (att.stato === 'M' || att.stato === 'F' || att.stato === 'A') ? (
                              <span className={`day-status ${att.stato === 'R' ? 'stato-r' : 'stato-altro'}`}>{att.stato}</span>
                            ) : (
                              <>
                                {att.stato && ['R', 'TS', 'P', 'PIOG'].includes(att.stato) && <span className={`day-status ${att.stato === 'R' ? 'stato-r' : 'stato-altro'}`}>{att.stato}</span>}
                              </>
                            )}
                            {(att.ore_lavorate > 0 || att.ore_straordinario > 0 || att.ore_reperibilita > 0 || att.ore_trasferta > 0 || att.ore_permesso > 0) && (
                              <div className="hours-display">
                                {att.ore_lavorate > 0 && <span className="hour-lavorate">{att.ore_lavorate}</span>}
                                {att.ore_straordinario > 0 && <span className="hour-straordinario">+{att.ore_straordinario}</span>}
                                {att.ore_reperibilita > 0 && <span className="hour-reperibilita">R:{att.ore_reperibilita}</span>}
                                {att.ore_trasferta > 0 && <span className="hour-trasferta">T:{att.ore_trasferta}</span>}
                                {att.ore_permesso > 0 && <span className="hour-permesso">P:{att.ore_permesso}</span>}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Matr</th><th>Nome</th><th>Cogn</th><th>Ore</th><th>Stra</th><th>Trasf</th><th>Reper</th><th>Perm</th><th>R</th><th>M</th><th>P</th><th>F</th><th>Ore TS</th><th>A</th><th>PIOG</th>
                {allCantieri && allCantieri.map(c => <th key={c.id}>{c.codice} - {c.nome}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={i}>
                  <td><span className="code">{r.matricola}</span></td>
                  <td>{r.nome}</td><td>{r.cognome}</td>
                  <td>{r.totale_ore}</td><td>{r.totale_straordinario}</td><td>{r.totale_trasferta}</td><td>{r.totale_reperibilita}</td><td>{r.totale_permesso}</td>
                  <td>{r.giorni_reperibile}</td><td>{r.giorni_malattia}</td><td>{r.giorni_permesso}</td>
                  <td>{r.giorni_ferie}</td><td>{r.ore_turno_sabato}</td><td>{r.giorni_assenza || 0}</td><td>{r.giorni_pioggia || 0}</td>
                  {allCantieri && allCantieri.map(c => {
                    const workerCantiere = r.cantieri && r.cantieri.find(cc => cc.id === c.id);
                    return <td key={c.id}>{workerCantiere ? workerCantiere.ore : ''}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}
    </div>
  );
}

function Users({ role }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ username: '', password: '', role: 'operatore' });
  const [editing, setEditing] = useState(null);

  useEffect(() => { if (role === 'admin') loadUsers(); }, [role]);

  const loadUsers = async () => {
    try { setUsers(await apiCall('/api/users')); } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await apiCall(`/api/users/${editing}`, { method: 'PUT', body: JSON.stringify(form) });
      else await apiCall('/api/users', { method: 'POST', body: JSON.stringify(form) });
      setForm({ username: '', password: '', role: 'operatore' });
      setEditing(null);
      loadUsers();
    } catch (err) { alert(err.message); }
  };

  const handleEdit = (u) => { setForm({ username: u.username, password: '', role: u.role }); setEditing(u.id); };
  const handleDelete = async (id) => {
    if (window.confirm('Eliminare?')) {
      try { await apiCall(`/api/users/${id}`, { method: 'DELETE' }); loadUsers(); } catch (err) { alert(err.message); }
    }
  };

  if (role !== 'admin') return null;

  return (
    <div className="page">
      <h2>Utenti</h2>
      <form className="form-box" onSubmit={handleSubmit}>
        <input placeholder="Username" value={form.username} onChange={e => setForm({...form, username: e.target.value})} required />
        <input placeholder="Password" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editing} />
        <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
          <option value="operatore">Operatore</option>
          <option value="admin">Admin</option>
        </select>
        <button type="submit">{editing ? 'Modifica' : 'Aggiungi'}</button>
        {editing && <button type="button" onClick={() => { setEditing(null); setForm({ username: '', password: '', role: 'operatore' }); }}>Annulla</button>}
      </form>
      <table className="data-table">
        <thead><tr><th>Username</th><th>Role</th><th>Azioni</th></tr></thead>
        <tbody>
          {users.map(u => (
            <tr key={u.id}><td>{u.username}</td><td><span className="code">{u.role}</span></td>
              <td><button onClick={() => handleEdit(u)}>Mod</button><button onClick={() => handleDelete(u.id)}>Elim</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  const [role, setRole] = useState(sessionStorage.getItem('role'));

  const handleLogin = (r) => setRole(r);
  const handleLogout = () => { sessionStorage.clear(); setRole(null); };

  if (!role) return <Login onLogin={handleLogin} />;

  return (
    <BrowserRouter>
      <div className="app-container">
        <nav className="sidebar">
          <div className="brand">Presenze</div>
          <NavLink to="/" end><span className="nav-icon">⊞</span><span className="nav-label">Dashboard</span></NavLink>
          <NavLink to="/workers"><span className="nav-icon">◎</span><span className="nav-label">Operai</span></NavLink>
          <NavLink to="/sites"><span className="nav-icon">◇</span><span className="nav-label">Cantieri</span></NavLink>
          <NavLink to="/reports"><span className="nav-icon">≡</span><span className="nav-label">Report</span></NavLink>
          {role === 'admin' && <NavLink to="/users"><span className="nav-icon">◉</span><span className="nav-label">Utenti</span></NavLink>}
          <NavLink to="/settings"><span className="nav-icon">⊙</span><span className="nav-label">Impost.</span></NavLink>
          <button className="logout" onClick={handleLogout}><span className="nav-icon">↩</span><span className="nav-label">Esci</span></button>
        </nav>
        <main className="main">
          <PageHeader />
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/workers" element={<Workers role={role} />} />
            <Route path="/sites" element={<Sites role={role} />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/users" element={<Users role={role} />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </BrowserRouter>
  );
}

function Settings() {
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState('');
  const [code2fa, setCode2fa] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => { loadStatus(); }, []);

  const loadStatus = async () => {
    try {
      const res = await apiCall('/api/2fa/status');
      setTwoFAEnabled(res.enabled);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleToggle2FA = async () => {
    setLoading(true);
    setMessage('');
    try {
      if (!twoFAEnabled) {
        const res = await apiCall('/api/2fa/setup', { method: 'POST', body: JSON.stringify({ enable: true }) });
        setQrCode(res.qrCode);
        setMessage(res.message + ' Inserisci il codice qua sotto per confermare.');
      } else {
        await apiCall('/api/2fa/setup', { method: 'POST', body: JSON.stringify({ enable: false }) });
        setTwoFAEnabled(false);
        setQrCode('');
        setMessage('2FA disabilitato');
      }
    } catch (err) { setMessage(err.message); }
    finally { setLoading(false); }
  };

  const handleConfirm2FA = async () => {
    setLoading(true);
    try {
      await apiCall('/api/2fa/confirm', { method: 'POST', body: JSON.stringify({ code2fa }) });
      setTwoFAEnabled(true);
      setQrCode('');
      setCode2fa('');
      setMessage('2FA attivato correttamente!');
    } catch (err) { setMessage(err.message); }
    finally { setLoading(false); }
  };

  if (loading) return <div>Caricamento...</div>;

  return (
    <div className="page">
      <h2>Impostazioni</h2>
      <div className="settings-section">
        <h3>Autenticazione a due fattori (Google Authenticator)</h3>
        <div className="settings-row">
          <label>Stato:</label>
          <span>{twoFAEnabled ? 'Attivo' : 'Non attivo'}</span>
        </div>
        <button onClick={handleToggle2FA} disabled={loading}>
          {twoFAEnabled ? 'Disabilita 2FA' : 'Abilita 2FA'}
        </button>
        {qrCode && (
          <div style={{marginTop: '16px'}}>
            <p style={{marginBottom: '12px'}}>Scansiona questo QR code con Google Authenticator:</p>
            <img src={qrCode} alt="QR Code 2FA" style={{width: '200px', marginBottom: '12px', display: 'block'}} />
            <div className="settings-row">
              <label>Codice:</label>
              <input 
                type="text" 
                placeholder="Inserisci codice a 6 cifre" 
                value={code2fa} 
                onChange={e => setCode2fa(e.target.value)} 
                maxLength={6}
                style={{maxWidth: '150px'}}
              />
              <button onClick={handleConfirm2FA} disabled={loading}>Conferma</button>
            </div>
          </div>
        )}
        {message && <p className="message" style={{marginTop: '12px'}}>{message}</p>}
      </div>
      <BackupSection />
    </div>
  );
}

function BackupSection() {
  const [config, setConfig] = useState(null);
  const [backups, setBackups] = useState([]);
  const [localPath, setLocalPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ frequency: 'daily', max_backups: 7, nextcloud_url: '', nextcloud_user: '', nextcloud_pass: '', nextcloud_path: '' });
  const [importFile, setImportFile] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [cfgResult, listResult] = await Promise.allSettled([
        apiCall('/api/backup/config'),
        apiCall('/api/backup/list')
      ]);
      if (cfgResult.status === 'fulfilled') {
        const cfg = cfgResult.value;
        setConfig(cfg);
        if (cfg) setForm({
          frequency: cfg.frequency,
          max_backups: cfg.max_backups,
          nextcloud_url: cfg.nextcloud_url || '',
          nextcloud_user: cfg.nextcloud_user || '',
          nextcloud_pass: cfg.nextcloud_pass || '',
          nextcloud_path: cfg.nextcloud_path || ''
        });
      } else {
        console.error('backup/config error:', cfgResult.reason);
      }
      if (listResult.status === 'fulfilled') {
        setBackups(listResult.value.files || []);
        setLocalPath(listResult.value.path || '');
      } else {
        console.error('backup/list error:', listResult.reason);
      }
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true); setMessage('');
    try {
      await apiCall('/api/backup/config', { method: 'PUT', body: JSON.stringify(form) });
      setMessage('Configurazione salvata!');
      loadData();
    } catch (err) { setMessage(err.message); }
    finally { setSaving(false); }
  };

  const handleTestNextcloud = async () => {
    setSaving(true); setMessage('');
    try {
      const res = await apiCall('/api/backup/test-nextcloud', { method: 'POST', body: JSON.stringify(form) });
      setMessage(res.message);
    } catch (err) { setMessage('ERRORE: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleBackupNow = async () => {
    setSaving(true); setMessage('');
    try {
      const res = await apiCall('/api/backup/now', { method: 'POST' });
      let msg = `Backup locale eseguito: ${res.filename}.`;
      if (res.cloudStatus) {
        msg += ` Cloud: ${res.cloudStatus.message}`;
      }
      setMessage(msg);
      loadData();
    } catch (err) { setMessage(err.message); }
    finally { setSaving(false); }
  };

  const handleRestore = async (filename) => {
    if (!window.confirm('Ripristinare questo backup? I dati attuali verranno sovrascritti.')) return;
    setSaving(true); setMessage('');
    try {
      await apiCall(`/api/backup/restore/${filename}`, { method: 'POST' });
      setMessage('Backup ripristinato!');
      loadData();
    } catch (err) { setMessage(err.message); }
    finally { setSaving(false); }
  };

  const handleImport = async () => {
    if (!importFile) return;
    if (!window.confirm(`Importare "${importFile.name}"?\n\nATTENZIONE: tutti i dati attuali del database verranno sovrascritti.`)) return;
    setSaving(true); setMessage('');
    try {
      const sql = await importFile.text();
      const res = await apiCall('/api/backup/import', { method: 'POST', body: JSON.stringify({ sql }) });
      setMessage(res.message || 'Import completato');
      setImportFile(null);
      loadData();
    } catch (err) { setMessage('Errore import: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Eliminare definitivamente il file ${filename}?`)) return;
    setSaving(true); setMessage('');
    try {
      await apiCall(`/api/backup/${filename}`, { method: 'DELETE' });
      setMessage('Backup eliminato.');
      loadData();
    } catch (err) { setMessage(err.message); }
    finally { setSaving(false); }
  };

  if (loading) return <div>Caricamento...</div>;

  return (
    <div className="settings-section">
      <h3>Backup Database</h3>
      <div className="settings-row">
        <label>Frequenza:</label>
        <select value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}>
          <option value="manual">Manuale</option>
          <option value="daily">Giornaliero (mezzanotte)</option>
          <option value="weekly">Settimanale (domenica)</option>
          <option value="monthly">Mensile (1° del mese)</option>
        </select>
      </div>
      <div className="settings-row">
        <label>Backup da conservare:</label>
        <input type="number" min="1" max="100" value={form.max_backups} onChange={e => setForm({...form, max_backups: parseInt(e.target.value)})} />
      </div>
      <div className="settings-row">
        <label>URL Nextcloud:</label>
        <input type="text" placeholder="https://nextcloud.example.com" value={form.nextcloud_url} onChange={e => setForm({...form, nextcloud_url: e.target.value})} />
      </div>
      <div className="settings-row">
        <label>Username Nextcloud:</label>
        <input type="text" value={form.nextcloud_user} onChange={e => setForm({...form, nextcloud_user: e.target.value})} />
      </div>
      <div className="settings-row">
        <label>Password Nextcloud:</label>
        <input type="password" value={form.nextcloud_pass} onChange={e => setForm({...form, nextcloud_pass: e.target.value})} />
      </div>
      <div className="settings-row">
        <label>Cartella Nextcloud:</label>
        <input type="text" placeholder="/Backups (opzionale)" value={form.nextcloud_path} onChange={e => setForm({...form, nextcloud_path: e.target.value})} />
      </div>
      <div className="settings-actions">
        <button onClick={handleSave} disabled={saving}>Salva Configurazione</button>
        <button onClick={handleTestNextcloud} disabled={saving} className="btn-secondary">Verifica Connessione</button>
        <button onClick={handleBackupNow} disabled={saving}>Esegui Backup Ora</button>
      </div>
      {config && config.last_backup && <p className="backup-info">Ultimo backup: {new Date(config.last_backup).toLocaleString()}</p>}
      {localPath && <p className="backup-info">Percorso locale: <span className="code">{localPath}</span></p>}
      {message && <p className="message" style={{marginTop: '8px'}}>{message}</p>}
      <h4 style={{marginTop: '20px', marginBottom: '12px'}}>Backup disponibili (Locali):</h4>
      {backups.length === 0 ? <p className="backup-info">Nessun backup disponibile</p> : (
        <table className="data-table">
          <thead><tr><th>File</th><th>Data</th><th>Dimensione</th><th>Azioni</th></tr></thead>
          <tbody>
            {backups.map(b => (
              <tr key={b.filename}>
                <td><span className="code">{b.filename}</span></td>
                <td>{new Date(b.created).toLocaleString()}</td>
                <td>{(b.size / 1024).toFixed(1)} KB</td>
                <td>
                  <button onClick={async () => {
                    try {
                      const token = sessionStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/backup/download/${b.filename}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!response.ok) throw new Error('Errore durante il download');
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = b.filename;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                    } catch (err) { alert(err.message); }
                  }} className="btn-small">Scarica</button>
                  <button onClick={() => handleRestore(b.filename)} className="btn-small">Ripristina</button>
                  <button onClick={() => handleDeleteBackup(b.filename)} className="btn-small btn-danger">Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div style={{marginTop: '24px', borderTop: '1px solid var(--border)', paddingTop: '16px'}}>
        <h4 style={{marginBottom: '8px'}}>Importa Backup da File Locale</h4>
        <p className="backup-info">Carica un file .sql precedentemente scaricato per ripristinare il database. I dati attuali verranno sovrascritti.</p>
        <div style={{display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px'}}>
          <input type="file" accept=".sql" onChange={e => setImportFile(e.target.files[0] || null)} />
          <button onClick={handleImport} disabled={!importFile || saving}>Importa</button>
        </div>
        {importFile && <p className="backup-info" style={{marginTop: '6px'}}>File: {importFile.name} ({(importFile.size / 1024).toFixed(1)} KB)</p>}
      </div>
    </div>
  );
}

export default App;